import { Caps } from "../core/Caps.js";
import { Text } from "../text/Text.js";
import { object } from "./ObjectUtil.js";
import { wait } from "./Wait.js";

export type PrintMode = "iframe" | "hide";

export interface PrintOptions {
    element: HTMLElement;
    mode?: "iframe" | "isolate";
    showOverlay?: boolean;
}

export class Print {

    private static _focusedElement: HTMLElement | null = null;
    private static _overlay: HTMLDivElement | null;
    private static _options: PrintOptions | null = null;

    public static async print(options: PrintOptions): Promise<boolean> {

        this._options = object.merge({
            mode: "isolate",
            showOverlay: false,
        } as PrintOptions, options);

        if (this._options.mode === "isolate") {
            await this.printElementIsolate();
        }
        else if (this._options.mode === "iframe") {
            await this.printElementIframe();
        }

        return true;
    }

    private static _elementsToHide: Element[] = [];
    private static _printId: number = 0;

    private static async printElementIsolate() {

        var printId = ++this._printId;

        var e = this._options?.element;

        if (!e) {
            return;
        }

        this.clearElementsToHide();
        this._elementsToHide = [];

        var parent: HTMLElement | null = e;
        while (parent) {

            if (parent !== e) {
                parent.setAttribute("data-e5print", "parent");
                this._elementsToHide.push(parent);
            }

            var p = parent.previousElementSibling;
            while (p) {
                p.setAttribute("data-e5print", "prev");
                this._elementsToHide.push(p);
                p = p.previousElementSibling;
            }

            var n = parent.nextElementSibling;
            while (n) {
                n.setAttribute("data-e5print", "next");
                this._elementsToHide.push(n);
                n = n.nextElementSibling;
            }

            parent = parent.parentElement;
        }

        // var wrapper = document.createElement("div");
        // wrapper.innerHTML = `
        // <table>
        //     <thead>
        //         <tr>
        //             <td>
        //                 <div class="header-space">Header</div>
        //             </td>
        //         </tr>
        //     </thead>
        //     <tbody>
        //         <tr>
        //             <td>
        //                 <div class="content"></div>
        //             </td>
        //         </tr>
        //     </tbody>
        //     <tfoot>
        //         <tr>
        //             <td>
        //                 <div class="footer-space">Footer</div>
        //             </td>
        //         </tr>
        //     </tfoot>
        // </table>
        // <div class="header">Header</div>
        // <div class="footer">Footer</div>"
        // `;
        // var content = wrapper.querySelector(".content");
        // var children:Node[] = [];
        // e.childNodes.forEach((child) => {
        //     children.push(child);
        //     content.appendChild(child);
        // });
        // e.appendChild(wrapper);


        this.beforePrint();
        window.print();

        //Bug on Android/iOS...
        await wait(500);

        // children.forEach((child) => {
        //     e.appendChild(child);
        // });
        // wrapper.remove();

        this.afterPrint();

        setTimeout(() => {
            if (this._printId === printId) {
                this.clearElementsToHide();
            }
        }, 2000);
    }

    private static clearElementsToHide(): void {
        if (this._elementsToHide) {
            for (var a of this._elementsToHide) {
                a.removeAttribute("data-e5print");
            }
            this._elementsToHide = [];
        }
    }

    private static async printElementIframe() {
        var e = this._options?.element;
        if (!e) {
            return;
        }

        const allCSS = Array.from(document.styleSheets)
            .map(styleSheet => {
                try {
                    return Array.from(styleSheet.cssRules)
                        .map(rule => {
                            return this.makeAbs(rule.cssText, styleSheet);
                        })
                        .join('');
                } catch (e) {
                    console.log('Access to stylesheet %s is denied. Ignoring...', styleSheet.href);
                }
            })
            .filter(Boolean)
            .join('\n');

        var html = `<html><head><style>${allCSS}</style></head><body>${e.outerHTML}</body></html>`;


        var frame = document.createElement("iframe");
        frame.style.visibility = "hidden";
        frame.style.position = "fixed";
        frame.style.top = "0";
        frame.style.left = "0";
        frame.style.width = "2px";
        frame.style.height = "2px";
        frame.style.pointerEvents = "none";
        document.body.appendChild(frame);

        var win = frame.contentWindow;
        if (!win) {
            return;
        }

        win.document.open();
        win.document.write(html);
        win.document.close();

        var ps: Promise<any>[] = [];
        win?.document.querySelectorAll("img").forEach((img: HTMLImageElement) => {
            ps.push(new Promise<any>((resolve, reject) => {
                console.log(img);
                if (img.complete) {
                    resolve(null);
                }
                else {
                    img.onload = resolve,
                        img.onerror = resolve;
                }
            }));
        });
        await Promise.all(ps);
        await win.document.fonts.ready;

        this.beforePrint();
        win.focus();
        win.print();
        frame.remove();
        this.afterPrint();
    }

    private static beforePrint(): void {
        this._focusedElement = document.activeElement as HTMLElement;

        if (!this._options) {
            return;
        }

        if (this._options.showOverlay) {
            this._overlay = document.createElement("div");
            this._overlay.className = "e5-print__overlay";
            this._overlay.innerHTML = '<span class="e5-print__overlay-icon fal fa-print"></span>';
            document.body.appendChild(this._overlay);
        }
    }

    private static afterPrint(): void {
        if (this._focusedElement && typeof this._focusedElement.focus === "function") {
            this._focusedElement.focus();
        }
        this._focusedElement = null;

        if (this._overlay) {
            this._overlay.remove();
            this._overlay = null;
        }
    }

    private static makeAbs(css: string, styleSheet: CSSStyleSheet): string {

        if (!styleSheet.href) {
            throw new Error("Missing href of the StyleSheet");
        }

        var urlRE = /\burl\s*\(\s*([^\)]*|"[^"]*"|'[^']')\s*\)/g;
        // urlRE = /url\((?!['"]?(?:data):)['"]?([^'"\)]*)['"]?\)/g;
        var schemaRE = /^(https?|file|data):/;
        var match;
        var url;

        return css.replace(urlRE, (match: string, url: string) => {
            url = url.trim();

            if (styleSheet.href) {
                // Strips the quotes if present
                if (url[0] === '"' || url[0] === "'")
                    url = url.slice(1, -1);

                url = resolvePath(styleSheet.href, url);
            }

            return `url(${url})`;
        });

        function resolvePath(base: string, url: string) {
            // The path is either absolute or a data URI
            if (schemaRE.test(url)) return url;

            // The path refers to another domain with the same schema
            if (url.substring(0, 2) === "//")
                return location.protocol + url;

            // The path is relative to the host's root
            if (url[0] === "/")
                return location.protocol + "//" + location.hostname + url;

            // Gets the stylesheet's dirname
            var i = base.lastIndexOf("/");
            base = base.substring(0, i + 1);

            return base + url;
        }
    }
}

export class PrintLayout {

    public readonly element: HTMLDivElement;

    constructor() {
        this.element = document.createElement("div");
        this.element.className = "e5-print__layout";
        if (Caps.isFirefox) {
            this.element.classList.add("e5-print__layout--firefox");
        }
        this.element.innerHTML = `
        <div class="e5-print__frontpage"></div>
        <div class="e5-print__header"></div>
        <div class="e5-print__footer"></div>
        <table>
            <thead>
                <tr>
                    <td>
                        <div class="e5-print__header-space"></div>
                    </td>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>
                        <div class="e5-print__content"></div>
                    </td>
                </tr>
            </tbody>
            <tfoot>
                <tr>
                    <td>
                        <div class="e5-print__footer-space"></div>
                    </td>
                </tr>
            </tfoot>
        </table>`;
    }

    public get frontpage(): HTMLDivElement | null {
        return this.element.querySelector(".e5-print__frontpage");
    }

    public get header(): HTMLDivElement | null {
        return this.element.querySelector(".e5-print__header");
    }

    public get content(): HTMLDivElement | null {
        return this.element.querySelector(".e5-print__content");
    }

    public get footer(): HTMLDivElement | null {
        return this.element.querySelector(".e5-print__footer");
    }

    public sync(): void {
        this.frontpage?.classList.toggle("e5-hidden", !this.frontpage.innerHTML);

        var headerSpace = this.element.querySelector(".e5-print__header-space");
        if (this.header && headerSpace) {
            headerSpace.innerHTML = this.header.outerHTML;
        }

        var footerSpace = this.element.querySelector(".e5-print__footer-space");
        if (footerSpace && this.footer) {
            footerSpace.innerHTML = this.footer.outerHTML;
        }
    }
}